import React from "react";
import { FaChevronRight } from "react-icons/fa";

/**
 * Props for ContextMenuItem component
 */
export interface ContextMenuItemProps {
  /** Click handler */
  onClick?: () => void;
  /** Keyboard shortcut to display */
  shortcut?: string;
  /** Whether this is a destructive action */
  danger?: boolean;
  /** Whether this item has a submenu */
  hasSubmenu?: boolean;
  /** Whether this item is disabled */
  disabled?: boolean;
  /** Content to render inside the item */
  children: React.ReactNode;
}

/**
 * A single item in a context menu.
 * Supports shortcuts, danger styling, submenus, and disabled states.
 * Uses sidebar-specific CSS variables for consistent styling.
 */
export const ContextMenuItem: React.FC<ContextMenuItemProps> = ({
  onClick,
  shortcut,
  danger,
  hasSubmenu,
  disabled,
  children,
}) => (
  <button
    onClick={disabled ? undefined : onClick}
    disabled={disabled}
    className={`w-full flex items-center justify-between px-3 py-2 text-sm transition-colors ${
      disabled
        ? "text-[var(--arc-sidebar-text-secondary)] opacity-50 cursor-not-allowed"
        : danger
        ? "text-red-400 hover:bg-red-500/20"
        : "text-[var(--arc-sidebar-text-primary)] hover:bg-[var(--arc-sidebar-accent-glow)]"
    }`}
  >
    <span className="flex items-center gap-2">{children}</span>
    {shortcut && !hasSubmenu && (
      <span className="text-[var(--arc-sidebar-text-secondary)] text-xs font-mono">{shortcut}</span>
    )}
    {hasSubmenu && <FaChevronRight size={10} className="text-[var(--arc-sidebar-text-secondary)]" />}
  </button>
);

ContextMenuItem.displayName = "ContextMenuItem";

/**
 * A divider line in a context menu.
 * Uses sidebar-specific CSS variables for consistent styling.
 */
export const ContextMenuDivider: React.FC = () => (
  <div className="my-1 border-t border-[var(--arc-sidebar-border-accent)]" />
);

ContextMenuDivider.displayName = "ContextMenuDivider";
